import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  CircleDollarSign,
  CreditCard,
  FileText,
  GraduationCap,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  Users,
  WalletCards,
} from 'lucide-react';
import api from '../utils/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [suspendModal, setSuspendModal] = useState({ show: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, course: null });
  const [importModal, setImportModal] = useState({ show: false, type: null });
  const [importData, setImportData] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [timeRange, setTimeRange] = useState('30');
  const [financeData, setFinanceData] = useState(null);
  const [financePayments, setFinancePayments] = useState([]);
  const [financeTotal, setFinanceTotal] = useState(0);
  const [financePage, setFinancePage] = useState(1);
  const [financeStatus, setFinanceStatus] = useState('');
  const [financeSearch, setFinanceSearch] = useState('');
  const [financeSearchInput, setFinanceSearchInput] = useState('');
  const [courseSortField, setCourseSortField] = useState('category');
  const [courseSortDir, setCourseSortDir] = useState('asc');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'sub-admin') {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [user, navigate]);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const fetchDashboardData = async () => {
    try {
      const [dashRes, usersRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users?limit=10'),
      ]);
      setDashboardData(dashRes.data);
      setUsers(usersRes.data.users);
      setTotalUsers(usersRes.data.total);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    try {
      const res = await api.get(`/admin/users?page=${page}&limit=20&search=${search}`);
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
      setUserPage(page);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await api.get('/admin/content');
      setCourses(res.data.content);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await api.get('/admin/payments?limit=50');
      setPayments(res.data.payments);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/admin/settings');
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [engagement, completion, earnings] = await Promise.all([
        api.get(`/analytics/engagement?period=${timeRange}`),
        api.get(`/analytics/completion?period=${timeRange}`),
        api.get(`/analytics/earnings?period=${timeRange}`),
      ]);
      setAnalytics({
        engagement: engagement.data,
        completion: completion.data,
        earnings: earnings.data,
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [timeRange]);

  const fetchFinanceData = useCallback(async () => {
    try {
      const res = await api.get(`/admin/analytics/financial?period=${timeRange}`);
      setFinanceData(res.data);
    } catch (error) {
      console.error('Error fetching finance data:', error);
    }
  }, [timeRange]);

  const fetchFinancePayments = useCallback(async (page = 1, status = '', search = '') => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      const res = await api.get(`/admin/payments?${params}`);
      setFinancePayments(res.data.payments);
      setFinanceTotal(res.data.total || 0);
      setFinancePage(page);
    } catch (error) {
      console.error('Error fetching finance payments:', error);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const [userActivity, coursePerformance, revenue] = await Promise.all([
        api.get('/admin/reports/user-activity?limit=20'),
        api.get('/admin/reports/course-performance'),
        api.get(`/admin/reports/revenue?period=${timeRange}`),
      ]);
      setReports({
        userActivity: userActivity.data.report,
        coursePerformance: coursePerformance.data.report,
        revenue: revenue.data,
      });
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  }, [timeRange]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers(userPage, searchTerm);
    if (activeTab === 'courses') { fetchCourses(); fetchCategories(); }
    if (activeTab === 'payments') fetchPayments();
    if (activeTab === 'settings') fetchSettings();
    if (activeTab === 'analytics') fetchAnalytics();
    if (activeTab === 'reports') fetchReports();
    if (activeTab === 'finance') { fetchFinanceData(); fetchFinancePayments(1, financeStatus, financeSearch); }
  }, [activeTab, fetchUsers, fetchCourses, fetchPayments, fetchSettings, fetchAnalytics, fetchReports, fetchFinanceData, fetchFinancePayments, userPage, searchTerm, timeRange, financeStatus, financeSearch]);

  const handleUserStatusToggle = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}`, { is_active: !currentStatus });
      setUsers(users.map(u =>
        u.id === userId ? { ...u, is_active: !currentStatus } : u
      ));
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}`, { role: newRole });
      setUsers(users.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleSuspendUser = async () => {
    if (!suspendModal.user) return;
    try {
      await api.post(`/admin/users/${suspendModal.user.id}/suspend`, {
        reason: suspendModal.reason,
        duration_days: suspendModal.duration || null,
      });
      setUsers(users.map(u =>
        u.id === suspendModal.user.id ? { ...u, is_active: false } : u
      ));
      setSuspendModal({ show: false, user: null });
      alert('User suspended successfully');
    } catch (error) {
      console.error('Error suspending user:', error);
      alert('Failed to suspend user');
    }
  };

  const handleUnsuspendUser = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/unsuspend`);
      setUsers(users.map(u =>
        u.id === userId ? { ...u, is_active: true } : u
      ));
    } catch (error) {
      console.error('Error unsuspending user:', error);
    }
  };

  const handlePublishToggle = async (courseId) => {
    try {
      await api.put(`/admin/courses/${courseId}/publish`);
      setCourses(courses.map(c =>
        c.id === courseId ? { ...c, is_published: !c.is_published } : c
      ));
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const handleDuplicateCourse = async (courseId) => {
    try {
      await api.post(`/admin/courses/${courseId}/duplicate`);
      fetchCourses();
      alert('Course duplicated successfully');
    } catch (error) {
      console.error('Error duplicating course:', error);
      alert('Failed to duplicate course');
    }
  };

  const handleDeleteCourse = async () => {
    if (!deleteModal.course) return;
    try {
      await api.delete(`/courses/${deleteModal.course.id}`);
      setCourses(courses.filter(c => c.id !== deleteModal.course.id));
      setDeleteModal({ show: false, course: null });
    } catch (error) {
      console.error('Error deleting course:', error);
      alert(error.response?.data?.error || 'Failed to delete course');
    }
  };

  const handleCourseSort = (field) => {
    if (courseSortField === field) {
      setCourseSortDir(courseSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setCourseSortField(field);
      setCourseSortDir('asc');
    }
  };

  const sortedFilteredCourses = [...(courses || [])]
    .filter(c => !categoryFilter || c.category_name === categoryFilter)
    .sort((a, b) => {
      let aVal = a[courseSortField] ?? '';
      let bVal = b[courseSortField] ?? '';
      if (courseSortField === 'lesson_count' || courseSortField === 'enrollment_count') {
        aVal = Number(aVal);
        bVal = Number(bVal);
        return courseSortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (aVal < bVal) return courseSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return courseSortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSettingUpdate = async (key, value) => {
    try {
      await api.put(`/admin/settings/${key}`, { value });
      setSettings(settings.map(s =>
        s.setting_key === key ? { ...s, setting_value: value } : s
      ));
    } catch (error) {
      console.error('Error updating setting:', error);
    }
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      const response = await api.get(`/admin/bulk/${type}/export?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json',
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        console.log('Export data:', response.data);
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Export failed');
    }
  };

  const handleReportExport = async (reportType, format = 'pdf') => {
    try {
      const mimeTypes = {
        pdf: 'application/pdf',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv'
      };

      const response = await api.get(`/admin/reports/${reportType}/export?format=${format}&period=${timeRange}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: mimeTypes[format] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_report.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Export failed');
    }
  };

  const handleImport = async () => {
    try {
      let data;
      try {
        data = JSON.parse(importData);
      } catch {
        alert('Invalid JSON format');
        return;
      }

      const endpoint = importModal.type === 'users'
        ? '/admin/bulk/users/import'
        : '/admin/bulk/enrollments/import';

      const payload = importModal.type === 'users'
        ? { users: data }
        : { enrollments: data };

      const res = await api.post(endpoint, payload);
      setImportResults(res.data);
    } catch (error) {
      console.error('Error importing:', error);
      alert('Import failed');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const overviewCards = [
    {
      label: 'Total Users',
      value: dashboardData?.stats.total_users || 0,
      hint: 'Platform wide',
      icon: Users,
      tone: 'green',
    },
    {
      label: 'Published Courses',
      value: dashboardData?.stats.total_courses || 0,
      hint: 'Live catalogue',
      icon: BookOpen,
      tone: 'teal',
    },
    {
      label: 'Total Enrollments',
      value: dashboardData?.stats.total_enrollments || 0,
      hint: 'Learners engaged',
      icon: GraduationCap,
      tone: 'blue',
    },
    {
      label: 'Lifetime Revenue',
      value: `$${(dashboardData?.stats.total_revenue || 0).toFixed(2)}`,
      hint: 'Completed payments',
      icon: CircleDollarSign,
      tone: 'amber',
    },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'courses', label: 'Content', icon: BookOpen },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'finance', label: 'Finance', icon: WalletCards },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'import-export', label: 'Import/Export', icon: ArrowLeftRight },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="page admin-dashboard">
      <div className="container">
        <section className="admin-hero">
          <div className="hero-content">
            <p className="eyebrow">
              <ShieldCheck size={15} aria-hidden="true" />
              Platform control center
            </p>
            <h1>Welcome back, {user?.first_name}</h1>
            <p>Monitor performance, manage users, and keep every course on track.</p>
          </div>
          <div className="hero-stats">
            {overviewCards.map(({ label, value, hint, icon: Icon, tone }) => (
              <div key={label} className={`hero-stat-card ${tone}`}>
                <span className="stat-icon"><Icon size={18} aria-hidden="true" /></span>
                <div>
                  <span className="stat-label">{label}</span>
                  <p className="stat-value">{value}</p>
                  <span className="stat-hint">{hint}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="admin-tabs" role="tablist" aria-label="Administration sections">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              className={activeTab === id ? 'active' : ''}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={15} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && dashboardData && (
          <div className="overview-panels">
            <div className="admin-section">
              <div className="section-header">
                <div>
                  <p className="label">Activity</p>
                  <h2>Recent Users</h2>
                </div>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recent_users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.first_name} {u.last_name}</td>
                        <td>{u.email}</td>
                        <td>
                          <span className={`role-badge ${u.role}`}>{u.role}</span>
                        </td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-section">
              <div className="section-header">
                <div>
                  <p className="label">Engagement</p>
                  <h2>Recent Enrollments</h2>
                </div>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recent_enrollments.map((e) => (
                      <tr key={e.id}>
                        <td>{e.email}</td>
                        <td>{e.course_title}</td>
                        <td>{new Date(e.enrolled_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-section">
            <div className="section-header">
              <div>
                <p className="label">Directory</p>
                <h2>User Management</h2>
              </div>
              <div className="header-actions">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    fetchUsers(1, e.target.value);
                  }}
                  className="search-input"
                />
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.first_name} {u.last_name}</td>
                      <td>{u.email}</td>
                      <td>
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        >
                          <option value="student">Student</option>
                          <option value="instructor">Instructor</option>
                          <option value="sub-admin">Sub-Admin</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                          {u.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td>{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                      <td className="action-buttons">
                        {u.is_active ? (
                          <button
                            onClick={() => setSuspendModal({ show: true, user: u, reason: '', duration: '' })}
                            className="btn-danger btn-small"
                          >
                            Suspend
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnsuspendUser(u.id)}
                            className="btn-primary btn-small"
                          >
                            Unsuspend
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button
                onClick={() => fetchUsers(userPage - 1, searchTerm)}
                disabled={userPage === 1}
              >
                Previous
              </button>
              <span>Page {userPage} of {Math.ceil(totalUsers / 20)}</span>
              <button
                onClick={() => fetchUsers(userPage + 1, searchTerm)}
                disabled={userPage >= Math.ceil(totalUsers / 20)}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="admin-section">
            <div className="section-header">
              <div>
                <p className="label">Content</p>
                <h2>Course Management</h2>
              </div>
              <button className="btn-primary" onClick={() => navigate('/create-course')}>
                + New Course
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Filter by Category:</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem' }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              {categoryFilter && (
                <button
                  className="btn-secondary btn-small"
                  onClick={() => setCategoryFilter('')}
                >
                  Clear Filter
                </button>
              )}
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      onClick={() => handleCourseSort('title')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Course {courseSortField === 'title' ? (courseSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </th>
                    <th
                      onClick={() => handleCourseSort('instructor_name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Instructor {courseSortField === 'instructor_name' ? (courseSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </th>
                    <th
                      onClick={() => handleCourseSort('category_name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Category {courseSortField === 'category_name' ? (courseSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </th>
                    <th
                      onClick={() => handleCourseSort('lesson_count')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Lessons {courseSortField === 'lesson_count' ? (courseSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </th>
                    <th
                      onClick={() => handleCourseSort('enrollment_count')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Enrollments {courseSortField === 'enrollment_count' ? (courseSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </th>
                    <th
                      onClick={() => handleCourseSort('is_published')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Status {courseSortField === 'is_published' ? (courseSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFilteredCourses.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state-cell">
                        <div className="empty-state large" style={{ margin: '10px 0', padding: '40px' }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                          </svg>
                          <h3>No Courses Available</h3>
                          <p>{categoryFilter ? `No courses in "${categoryFilter}" category.` : 'You haven\'t added any content yet. Click the "+ New Course" button to create your first course.'}</p>
                        </div>
                      </td>
                    </tr>
                  ) : sortedFilteredCourses.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="course-cell">
                          {c.thumbnail_url && (
                            <img src={c.thumbnail_url} alt="" className="course-thumb" />
                          )}
                          <span>{c.title}</span>
                        </div>
                      </td>
                      <td>{c.instructor_name}</td>
                      <td>{c.category_name}</td>
                      <td>{c.lesson_count || c.video_count || 0}</td>
                      <td>{c.enrollment_count}</td>
                      <td>
                        <span className={`status-badge ${c.is_published ? 'active' : 'inactive'}`}>
                          {c.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="action-buttons">
                        <button
                          onClick={() => navigate(`/edit-course/${c.id}`, { state: { from: '/admin', tab: 'courses' } })}
                          className="btn-secondary btn-small"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handlePublishToggle(c.id)}
                          className={`btn-small ${c.is_published ? 'btn-warning' : 'btn-primary'}`}
                        >
                          {c.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          onClick={() => handleDuplicateCourse(c.id)}
                          className="btn-secondary btn-small"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={() => setDeleteModal({ show: true, course: c })}
                          className="btn-danger btn-small"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-tab">
            <div className="analytics-header">
              <h2>Platform Analytics</h2>
              <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>

            {!analytics || (!analytics.completion?.overall?.total_enrollments && !analytics.earnings?.summary?.total_revenue) ? (
              <div className="empty-state large">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 20V10M12 20V4M6 20v-6"></path>
                </svg>
                <h3>No Analytics Data</h3>
                <p>Metrics will populate here once students begin interacting with the courses on your platform.</p>
              </div>
            ) : (
              <div className="analytics-grid">
                <div className="admin-section analytics-card">
                  <h3>Completion Rates</h3>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-value">{analytics.completion?.overall?.total_enrollments || 0}</span>
                      <span className="stat-label">Enrollments</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{analytics.completion?.overall?.completions || 0}</span>
                      <span className="stat-label">Completions</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{analytics.completion?.overall?.completion_rate || 0}%</span>
                      <span className="stat-label">Rate</span>
                    </div>
                  </div>
                </div>

                <div className="admin-section analytics-card">
                  <h3>Revenue Summary</h3>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-value">${Number(analytics.earnings?.summary?.total_revenue || 0).toFixed(2)}</span>
                      <span className="stat-label">Total Revenue</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">${Number(analytics.earnings?.summary?.period_revenue || 0).toFixed(2)}</span>
                      <span className="stat-label">Period Revenue</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{analytics.earnings?.summary?.total_customers || 0}</span>
                      <span className="stat-label">Customers</span>
                    </div>
                  </div>
                </div>

                <div className="admin-section analytics-card full-width">
                  <h3>Top Engaged Users</h3>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Courses</th>
                          <th>Lessons</th>
                          <th>Watch Time</th>
                          <th>Quiz Attempts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analytics.engagement?.top_engaged_users || []).map((u) => (
                          <tr key={u.id}>
                            <td>{u.first_name} {u.last_name}</td>
                            <td>{u.courses_enrolled}</td>
                            <td>{u.lessons_completed}</td>
                            <td>{Math.round(u.total_watch_time / 60)}m</td>
                            <td>{u.quiz_attempts}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="admin-section analytics-card">
                  <h3>Drop-off Analysis</h3>
                  <div className="dropoff-chart">
                    {(analytics.completion?.drop_off || []).map((bucket) => (
                      <div key={bucket.progress_bucket} className="dropoff-bar">
                        <span className="bucket-label">{bucket.progress_bucket}</span>
                        <div className="bar-container">
                          <div
                            className="bar-fill"
                            style={{
                              width: `${(bucket.count / Math.max(...analytics.completion.drop_off.map(b => b.count), 1)) * 100}%`
                            }}
                          />
                        </div>
                        <span className="bucket-count">{bucket.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="admin-section analytics-card">
                  <h3>Revenue by Course</h3>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Course</th>
                          <th>Sales</th>
                          <th>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analytics.earnings?.by_course || []).slice(0, 5).map((c) => (
                          <tr key={c.id}>
                            <td>{c.title}</td>
                            <td>{c.sales}</td>
                            <td>${Number(c.revenue).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="admin-section">
            <div className="section-header">
              <div>
                <p className="label">Transactions</p>
                <h2>Payment History</h2>
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Course</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state-cell">
                        <div className="empty-state inline">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="5" width="20" height="14" rx="2" />
                            <line x1="2" y1="10" x2="22" y2="10" />
                          </svg>
                          <p>No payments recorded yet</p>
                        </div>
                      </td>
                    </tr>
                  ) : payments.map((p) => (
                    <tr key={p.id}>
                      <td>{new Date(p.created_at).toLocaleDateString()}</td>
                      <td>{p.email}</td>
                      <td>{p.course_title}</td>
                      <td>${Number(p.amount).toFixed(2)}</td>
                      <td>
                        <span className={`status-badge ${p.status === 'completed' ? 'active' : 'inactive'}`}>
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'finance' && (
          <div className="finance-tab">
            <div className="analytics-header">
              <div>
                <p className="label">Financial Overview</p>
                <h2>Finance & Revenue</h2>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <select value={timeRange} onChange={(e) => { setTimeRange(e.target.value); }}>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last 12 months</option>
                </select>
                <button className="btn-primary btn-small" onClick={() => handleReportExport('financial', 'pdf')}>PDF</button>
                <button className="btn-secondary btn-small" onClick={() => handleReportExport('financial', 'xlsx')}>Excel</button>
                <button className="btn-secondary btn-small" onClick={() => handleReportExport('financial', 'csv')}>CSV</button>
              </div>
            </div>

            {financeData && (
              <>
                {/* Summary Cards */}
                <div className="hero-stats" style={{ marginBottom: '2rem' }}>
                  <div className="hero-stat-card">
                    <span className="stat-label">Total Revenue</span>
                    <p className="stat-value">${Number(financeData.summary?.total_revenue || 0).toFixed(2)}</p>
                    <span className="stat-hint">All time</span>
                  </div>
                  <div className="hero-stat-card">
                    <span className="stat-label">Period Revenue</span>
                    <p className="stat-value">${Number(financeData.summary?.period_revenue || 0).toFixed(2)}</p>
                    <span className="stat-hint">Last {timeRange} days</span>
                  </div>
                  <div className="hero-stat-card">
                    <span className="stat-label">Avg Order Value</span>
                    <p className="stat-value">${Number(financeData.summary?.avg_order_value || 0).toFixed(2)}</p>
                    <span className="stat-hint">Per transaction</span>
                  </div>
                  <div className="hero-stat-card">
                    <span className="stat-label">Customers</span>
                    <p className="stat-value">{financeData.summary?.total_customers || 0}</p>
                    <span className="stat-hint">{financeData.summary?.new_customers || 0} new this period</span>
                  </div>
                  <div className="hero-stat-card">
                    <span className="stat-label">Refunds</span>
                    <p className="stat-value">${Number(financeData.refunds?.refund_amount || 0).toFixed(2)}</p>
                    <span className="stat-hint">{financeData.refunds?.refund_count || 0} orders · {financeData.refunds?.refund_rate || 0}% rate</span>
                  </div>
                </div>

                <div className="reports-grid">
                  {/* Revenue Trend */}
                  {financeData.revenue_trend?.length > 0 && (
                    <div className="admin-section full-width">
                      <div className="section-header"><h3>Revenue Trend</h3></div>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Period</th>
                              <th>Revenue</th>
                              <th>Transactions</th>
                              <th>Customers</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financeData.revenue_trend.map((row, i) => (
                              <tr key={i}>
                                <td>{row.period}</td>
                                <td>${Number(row.revenue || 0).toFixed(2)}</td>
                                <td>{row.transactions}</td>
                                <td>{row.customers}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Top Courses by Revenue */}
                  {financeData.top_courses?.length > 0 && (
                    <div className="admin-section">
                      <div className="section-header"><h3>Top Courses by Revenue</h3></div>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Course</th>
                              <th>Sales</th>
                              <th>Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financeData.top_courses.map((c) => (
                              <tr key={c.id}>
                                <td>{c.title}</td>
                                <td>{c.sales}</td>
                                <td>${Number(c.revenue || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Revenue by Category */}
                  {financeData.by_category?.length > 0 && (
                    <div className="admin-section">
                      <div className="section-header"><h3>Revenue by Category</h3></div>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Category</th>
                              <th>Sales</th>
                              <th>Revenue</th>
                              <th>Share</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financeData.by_category.map((cat, i) => (
                              <tr key={i}>
                                <td>{cat.category}</td>
                                <td>{cat.sales}</td>
                                <td>${Number(cat.revenue || 0).toFixed(2)}</td>
                                <td>{cat.percentage || 0}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Discount Code Usage */}
                  {financeData.discount_usage?.length > 0 && (
                    <div className="admin-section">
                      <div className="section-header"><h3>Discount Code Performance</h3></div>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Code</th>
                              <th>Uses</th>
                              <th>Discount Given</th>
                              <th>Order Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financeData.discount_usage.map((d, i) => (
                              <tr key={i}>
                                <td><code>{d.code}</code></td>
                                <td>{d.usage_count}</td>
                                <td>${Number(d.total_discount || 0).toFixed(2)}</td>
                                <td>${Number(d.total_order_value || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Transactions Table */}
            <div className="admin-section" style={{ marginTop: '2rem' }}>
              <div className="section-header">
                <div>
                  <p className="label">All Transactions</p>
                  <h3>Payment History</h3>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search by email or course..."
                  value={financeSearchInput}
                  onChange={(e) => setFinanceSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setFinanceSearch(financeSearchInput);
                      fetchFinancePayments(1, financeStatus, financeSearchInput);
                    }
                  }}
                  style={{ flex: 1, minWidth: '200px' }}
                />
                <select
                  value={financeStatus}
                  onChange={(e) => {
                    setFinanceStatus(e.target.value);
                    fetchFinancePayments(1, e.target.value, financeSearch);
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="refunded">Refunded</option>
                </select>
                <button
                  className="btn-secondary btn-small"
                  onClick={() => {
                    setFinanceSearch(financeSearchInput);
                    fetchFinancePayments(1, financeStatus, financeSearchInput);
                  }}
                >
                  Search
                </button>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Course</th>
                      <th>Amount</th>
                      <th>Discount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financePayments.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="empty-state-cell">
                          <div className="empty-state inline">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="2" y="5" width="20" height="14" rx="2" />
                              <line x1="2" y1="10" x2="22" y2="10" />
                            </svg>
                            <p>No transactions found</p>
                          </div>
                        </td>
                      </tr>
                    ) : financePayments.map((p) => (
                      <tr key={p.id}>
                        <td>{new Date(p.created_at).toLocaleDateString()}</td>
                        <td>
                          <div>{p.first_name} {p.last_name}</div>
                          <small style={{ color: 'var(--text-muted)' }}>{p.email}</small>
                        </td>
                        <td>{p.course_title}</td>
                        <td>${Number(p.amount).toFixed(2)}</td>
                        <td>
                          {p.discount_code ? (
                            <span>
                              <code>{p.discount_code}</code>
                              {p.discount_amount > 0 && <span style={{ color: 'var(--text-muted)' }}> −${Number(p.discount_amount).toFixed(2)}</span>}
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          <span className={`status-badge ${p.status === 'completed' ? 'active' : p.status === 'refunded' ? 'suspended' : 'inactive'}`}>
                            {p.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {financeTotal > 20 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    Showing {Math.min((financePage - 1) * 20 + 1, financeTotal)}–{Math.min(financePage * 20, financeTotal)} of {financeTotal}
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn-secondary btn-small"
                      disabled={financePage === 1}
                      onClick={() => fetchFinancePayments(financePage - 1, financeStatus, financeSearch)}
                    >
                      Previous
                    </button>
                    <button
                      className="btn-secondary btn-small"
                      disabled={financePage * 20 >= financeTotal}
                      onClick={() => fetchFinancePayments(financePage + 1, financeStatus, financeSearch)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="reports-tab">
            <div className="analytics-header">
              <h2>Reports & Analytics</h2>
              <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>

            {!reports || (!reports.coursePerformance?.length && !reports.userActivity?.length) ? (
              <div className="empty-state large">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <h3>No Reports Available</h3>
                <p>Detailed reports will be generated once your platform accumulates activity data.</p>
              </div>
            ) : (
              <div className="reports-grid">
                <div className="admin-section full-width">
                  <div className="section-header">
                    <h3>Course Performance</h3>
                    <div className="export-buttons">
                      <button
                        className="btn-primary btn-small"
                        onClick={() => handleReportExport('course-performance', 'pdf')}
                      >
                        PDF
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => handleReportExport('course-performance', 'xlsx')}
                      >
                        Excel
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => handleReportExport('course-performance', 'csv')}
                      >
                        CSV
                      </button>
                    </div>
                  </div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Course</th>
                          <th>Instructor</th>
                          <th>Enrollments</th>
                          <th>Completions</th>
                          <th>Avg Progress</th>
                          <th>Rating</th>
                          <th>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reports.coursePerformance || []).map((c) => (
                          <tr key={c.id}>
                            <td>{c.title}</td>
                            <td>{c.instructor_name}</td>
                            <td>{c.total_enrollments}</td>
                            <td>{c.completions}</td>
                            <td>{c.avg_progress}%</td>
                            <td>{Number(c.avg_rating).toFixed(1)}</td>
                            <td>${Number(c.total_revenue).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="admin-section">
                  <div className="section-header">
                    <h3>Student Progress</h3>
                    <div className="export-buttons">
                      <button
                        className="btn-primary btn-small"
                        onClick={() => handleReportExport('student-progress', 'pdf')}
                      >
                        PDF
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => handleReportExport('student-progress', 'xlsx')}
                      >
                        Excel
                      </button>
                    </div>
                  </div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Enrollments</th>
                          <th>Lessons</th>
                          <th>Last Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reports.userActivity || []).slice(0, 10).map((u) => (
                          <tr key={u.user_id}>
                            <td>{u.first_name} {u.last_name}</td>
                            <td>{u.enrollments}</td>
                            <td>{u.lessons_completed}</td>
                            <td>{u.last_activity ? new Date(u.last_activity).toLocaleDateString() : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="admin-section">
                  <div className="section-header">
                    <h3>Quiz Results</h3>
                    <div className="export-buttons">
                      <button
                        className="btn-primary btn-small"
                        onClick={() => handleReportExport('quiz-results', 'pdf')}
                      >
                        PDF
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => handleReportExport('quiz-results', 'xlsx')}
                      >
                        Excel
                      </button>
                    </div>
                  </div>
                  <div className="quiz-summary">
                    <p>View comprehensive quiz analytics including score distributions, pass rates, and difficulty analysis.</p>
                    <button
                      className="btn-secondary"
                      onClick={() => setActiveTab('analytics')}
                    >
                      View Quiz Analytics
                    </button>
                  </div>
                </div>

                <div className="admin-section">
                  <div className="section-header">
                    <h3>Engagement Time</h3>
                    <div className="export-buttons">
                      <button
                        className="btn-primary btn-small"
                        onClick={() => handleReportExport('engagement', 'pdf')}
                      >
                        PDF
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => handleReportExport('engagement', 'xlsx')}
                      >
                        Excel
                      </button>
                    </div>
                  </div>
                  <div className="engagement-summary">
                    <p>Track student engagement metrics including watch time, session duration, and activity patterns.</p>
                    <button
                      className="btn-secondary"
                      onClick={() => setActiveTab('analytics')}
                    >
                      View Engagement Analytics
                    </button>
                  </div>
                </div>

                <div className="admin-section">
                  <div className="section-header">
                    <h3>Financial Report</h3>
                    <div className="export-buttons">
                      <button
                        className="btn-primary btn-small"
                        onClick={() => handleReportExport('financial', 'pdf')}
                      >
                        PDF
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => handleReportExport('financial', 'xlsx')}
                      >
                        Excel
                      </button>
                    </div>
                  </div>
                  {reports.revenue?.summary && (
                    <div className="revenue-summary">
                      <div className="revenue-stat">
                        <span className="value">${Number(reports.revenue.summary.total_revenue).toFixed(2)}</span>
                        <span className="label">Total Revenue</span>
                      </div>
                      <div className="revenue-stat">
                        <span className="value">{reports.revenue.summary.total_transactions}</span>
                        <span className="label">Transactions</span>
                      </div>
                      <div className="revenue-stat">
                        <span className="value">${Number(reports.revenue.summary.avg_transaction).toFixed(2)}</span>
                        <span className="label">Avg Order</span>
                      </div>
                      <div className="revenue-stat">
                        <span className="value">{reports.revenue.summary.unique_customers}</span>
                        <span className="label">Customers</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'import-export' && (
          <div className="import-export-tab">
            <div className="admin-section">
              <h2>Import / Export Data</h2>
              <div className="import-export-grid">
                <div className="export-section">
                  <h3>Export Raw Data (CSV)</h3>
                  <div className="export-buttons">
                    <button className="btn-primary" onClick={() => handleExport('users', 'csv')}>
                      Export Users
                    </button>
                    <button className="btn-primary" onClick={() => handleExport('enrollments', 'csv')}>
                      Export Enrollments
                    </button>
                    <button className="btn-primary" onClick={() => handleExport('grades', 'csv')}>
                      Export Grades
                    </button>
                  </div>
                </div>

                <div className="import-section">
                  <h3>Import Data</h3>
                  <div className="import-buttons">
                    <button
                      className="btn-secondary"
                      onClick={() => setImportModal({ show: true, type: 'users' })}
                    >
                      Import Users
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => setImportModal({ show: true, type: 'enrollments' })}
                    >
                      Import Enrollments
                    </button>
                  </div>
                </div>
              </div>

              <div className="export-reports-section">
                <h3>Export Reports</h3>
                <p className="section-description">Download comprehensive reports in PDF or Excel format.</p>
                <div className="reports-export-grid">
                  <div className="report-export-card">
                    <h4>Student Progress Report</h4>
                    <p>Detailed progress tracking by student across all courses and modules.</p>
                    <div className="export-buttons">
                      <button className="btn-primary btn-small" onClick={() => handleReportExport('student-progress', 'pdf')}>
                        PDF
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('student-progress', 'xlsx')}>
                        Excel
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('student-progress', 'csv')}>
                        CSV
                      </button>
                    </div>
                  </div>

                  <div className="report-export-card">
                    <h4>Course Performance Report</h4>
                    <p>Course analytics including enrollments, completion rates, and revenue.</p>
                    <div className="export-buttons">
                      <button className="btn-primary btn-small" onClick={() => handleReportExport('course-performance', 'pdf')}>
                        PDF
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('course-performance', 'xlsx')}>
                        Excel
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('course-performance', 'csv')}>
                        CSV
                      </button>
                    </div>
                  </div>

                  <div className="report-export-card">
                    <h4>Quiz Results Report</h4>
                    <p>Quiz performance analytics including score distributions and pass rates.</p>
                    <div className="export-buttons">
                      <button className="btn-primary btn-small" onClick={() => handleReportExport('quiz-results', 'pdf')}>
                        PDF
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('quiz-results', 'xlsx')}>
                        Excel
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('quiz-results', 'csv')}>
                        CSV
                      </button>
                    </div>
                  </div>

                  <div className="report-export-card">
                    <h4>Engagement Time Report</h4>
                    <p>Student engagement metrics including watch time and activity patterns.</p>
                    <div className="export-buttons">
                      <button className="btn-primary btn-small" onClick={() => handleReportExport('engagement', 'pdf')}>
                        PDF
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('engagement', 'xlsx')}>
                        Excel
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('engagement', 'csv')}>
                        CSV
                      </button>
                    </div>
                  </div>

                  <div className="report-export-card">
                    <h4>Financial Analytics Report</h4>
                    <p>Revenue analysis by course, instructor, and time period.</p>
                    <div className="export-buttons">
                      <button className="btn-primary btn-small" onClick={() => handleReportExport('financial', 'pdf')}>
                        PDF
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('financial', 'xlsx')}>
                        Excel
                      </button>
                      <button className="btn-secondary btn-small" onClick={() => handleReportExport('financial', 'csv')}>
                        CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="import-format-help">
                <h4>Import Format Guide</h4>
                <div className="format-examples">
                  <div className="format-example">
                    <h5>Users JSON Format:</h5>
                    <pre>{`[
  {
    "email": "user@example.com",
    "first_name": "John",
    "last_name": "Doe",
    "role": "student",
    "password": "optional123"
  }
]`}</pre>
                  </div>
                  <div className="format-example">
                    <h5>Enrollments JSON Format:</h5>
                    <pre>{`[
  {
    "user_email": "user@example.com",
    "course_id": 1
  }
]`}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="admin-section settings-section">
            <div className="section-header">
              <div>
                <p className="label">Configuration</p>
                <h2>System Settings</h2>
              </div>
            </div>
            {settings.length === 0 ? (
              <div className="empty-state large">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <h3>Settings Unavailable</h3>
                <p>Failed to load system settings.</p>
              </div>
            ) : (
              <div className="settings-grid">
                {settings.map((setting) => (
                  <div key={setting.setting_key} className="setting-card">
                    <div className="setting-header">
                      <div className="setting-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <div className="setting-info">
                        <label>{setting.setting_key.replace(/_/g, ' ').toUpperCase()}</label>
                        <p>{setting.description}</p>
                      </div>
                    </div>
                    <div className="setting-control">
                      {setting.setting_value === 'true' || setting.setting_value === 'false' ? (
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={setting.setting_value === 'true'}
                            onChange={(e) => handleSettingUpdate(setting.setting_key, e.target.checked ? 'true' : 'false')}
                          />
                          <span className="slider"></span>
                        </label>
                      ) : (
                        <input
                          type="text"
                          className="setting-input"
                          value={setting.setting_value}
                          onChange={(e) => handleSettingUpdate(setting.setting_key, e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {suspendModal.show && (
        <div className="modal-overlay" onClick={() => setSuspendModal({ show: false, user: null })}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Suspend User</h3>
            <p>Suspending: {suspendModal.user?.email}</p>
            <div className="form-group">
              <label>Reason</label>
              <textarea
                value={suspendModal.reason || ''}
                onChange={(e) => setSuspendModal({ ...suspendModal, reason: e.target.value })}
                placeholder="Enter suspension reason..."
              />
            </div>
            <div className="form-group">
              <label>Duration (days, leave empty for permanent)</label>
              <input
                type="number"
                value={suspendModal.duration || ''}
                onChange={(e) => setSuspendModal({ ...suspendModal, duration: e.target.value })}
                placeholder="e.g., 30"
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setSuspendModal({ show: false, user: null })}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleSuspendUser}>
                Suspend User
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal.show && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ show: false, course: null })}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Course</h3>
            <p>Are you sure you want to permanently delete <strong>{deleteModal.course?.title}</strong>?</p>
            <p style={{ color: '#ef4444', fontSize: '0.875rem' }}>
              This will delete all modules, lessons, enrollments, and progress data. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteModal({ show: false, course: null })}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDeleteCourse}>
                Delete Course
              </button>
            </div>
          </div>
        </div>
      )}

      {importModal.show && (
        <div className="modal-overlay" onClick={() => setImportModal({ show: false, type: null })}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <h3>Import {importModal.type}</h3>
            <div className="form-group">
              <label>Paste JSON data:</label>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                rows={10}
                placeholder={`Paste your ${importModal.type} JSON here...`}
              />
            </div>
            {importResults && (
              <div className="import-results">
                <p>Imported: {importResults.imported}</p>
                <p>Failed: {importResults.failed}</p>
                {importResults.errors.length > 0 && (
                  <div className="import-errors">
                    <p>Errors:</p>
                    <ul>
                      {importResults.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err.email || err.user_email}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setImportModal({ show: false, type: null });
                  setImportData('');
                  setImportResults(null);
                }}
              >
                Close
              </button>
              <button className="btn-primary" onClick={handleImport}>
                Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
